import { useId, type ReactNode } from 'react';

import { AlertCircle } from 'lucide-react';

import { cx } from '@/lib/cx';

import styles from './Field.module.css';

export type FieldProps = {
  label: string;
  required?: boolean;
  /** Shown below the control while there is no error. */
  hint?: ReactNode;
  error?: string | undefined;
  className?: string;
  /**
   * Receives the ids to wire up.
   *
   * The control has to carry `aria-describedby` pointing at the error, and the error
   * has to have that id. Doing it here means no form can forget (plan 9.5).
   */
  children: (ids: {
    id: string;
    describedBy: string | undefined;
    invalid: boolean;
  }) => ReactNode;
};

export function Field({ label, required, hint, error, className, children }: FieldProps) {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;

  const describedBy = error ? errorId : hint ? hintId : undefined;

  return (
    <div className={cx(styles.field, className)}>
      <label className={styles.label} htmlFor={id}>
        {label}
        {required ? (
          <span className={styles.required} aria-hidden="true">
            *
          </span>
        ) : null}
      </label>

      {children({ id, describedBy, invalid: Boolean(error) })}

      {error ? (
        // Announced when it appears, so a screen-reader user is not left wondering
        // why submitting did nothing.
        <p className={styles.error} id={errorId} role="alert">
          <AlertCircle size={13} aria-hidden="true" />
          {error}
        </p>
      ) : hint ? (
        <p className={styles.hint} id={hintId}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}
